import api from './api';
import type {
  RolePermissions,
  UserRole,
  ProjectPermissions,
  MyPermissions,
  ResourceName,
  PermissionAction
} from '../types/permissions';

/**
 * Get project permissions configuration (owner/admin only)
 */
export const getProjectPermissions = async (
  projectId: string
): Promise<ProjectPermissions> => {
  const response = await api.get(`/projects/${projectId}/permissions`);
  return response.data.data;
};

/**
 * Update project permissions configuration (owner/admin only)
 * Only member and viewer roles can be modified
 */
export const updateProjectPermissions = async (
  projectId: string,
  permissions: {
    member?: RolePermissions;
    viewer?: RolePermissions;
  }
): Promise<ProjectPermissions> => {
  const response = await api.put(`/projects/${projectId}/permissions`, permissions);
  return response.data.data;
};

/**
 * Get current user's role and permissions for a project
 */
export const getMyPermissions = async (
  projectId: string
): Promise<MyPermissions> => {
  const response = await api.get(`/projects/${projectId}/permissions/my-role`);
  return response.data.data;
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (
  permissions: RolePermissions | undefined | null,
  resource: ResourceName,
  action: PermissionAction
): boolean => {
  if (!permissions) return false;
  return permissions[resource][action];
};

/**
 * Check if user role is owner or admin
 */
export const isOwnerOrAdmin = (role: UserRole | undefined): boolean => {
  return role === 'owner' || role === 'admin';
};

/**
 * Get readable role name in Portuguese
 */
export const getRoleName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    owner: 'Proprietário',
    admin: 'Administrador',
    member: 'Membro',
    viewer: 'Visualizador'
  };
  return roleNames[role] || role;
};

/**
 * Get readable resource name in Portuguese
 */
export const getResourceName = (resource: ResourceName): string => {
  const resourceNames: Record<ResourceName, string> = {
    projects: 'Projetos',
    tasks: 'Tarefas',
    serviceOrders: 'Ordens de Serviço',
    hoursBank: 'Banco de Horas',
    team: 'Equipe',
    settings: 'Configurações'
  };
  return resourceNames[resource] || resource;
};
